import { Lightning } from '@lightningjs/sdk';
import { SpeechType } from '@lightningjs/ui-components';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { constants } from 'aliases';

export type StatefulIcon = {
  selected?: Lightning.Element.PatchTemplate<any>;
  unselected?: Lightning.Element.PatchTemplate<any>;
};

export type BackgroundColor = {
  focused?: number;
  unfocused?: number;
};

export interface FocusableIconToggleTemplateSpec
  extends Lightning.Component.TemplateSpec {
  title: SpeechType;
  selected: boolean;
  icons: StatefulIcon;
  backgroundColors: BackgroundColor;
  action: string;

  Icon: Lightning.textures.ImageTexture;
  Background: Lightning.textures.RectangleTexture;
}

export default class FocusableIconToggle
  extends HoverableComponent<FocusableIconToggleTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<FocusableIconToggleTemplateSpec>
{
  private _title: SpeechType;
  private _selected = false;
  private _icon: StatefulIcon = {};
  private _action = '';
  private _backgroundColors: BackgroundColor = {};

  private _Icon = this.getByRef('Icon')!;
  private _Background = this.getByRef('Background')!;

  override set w(w: number) {
    super.w = w;
    this.update();
  }

  override get w(): number {
    return super.w;
  }

  override set h(h: number) {
    super.h = h;
    this.update();
  }

  override get h(): number {
    return super.h;
  }

  get title() {
    return this._title;
  }

  set title(title: FocusableIconToggleTemplateSpec['title']) {
    this._title = title;
  }

  get selected() {
    return this._selected;
  }

  set selected(value: boolean) {
    this._selected = value;
    this.updateIcon();
  }

  get icons() {
    return this._icon;
  }

  set icons(icon: StatefulIcon) {
    this._icon = icon;
    this.updateIcon();
  }

  get action() {
    return this._action;
  }

  set action(value: string) {
    this._action = value;
  }

  get backgroundColors() {
    return this._backgroundColors;
  }

  set backgroundColors(value: BackgroundColor) {
    this._backgroundColors = value;
    this.updateBackground();
  }

  static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
    return {
      flex: {
        direction: 'column',
      },
      Background: {
        flexItem: false,
        rect: true,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: 3,
        },
      },
      Icon: {
        mount: 0.5,
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
      },
    };
  }

  override _handleEnter() {
    this.selected = !this.selected;
    this.update();
    this.signal(this.action);
  }

  override _focus() {
    this.update();
  }

  override _unfocus() {
    this.update();
  }

  private update() {
    this.updateIcon();
    this.updateBackground();
  }

  private updateBackground() {
    const color = this.hasFocus()
      ? this.backgroundColors.focused
      : this.backgroundColors.unfocused;

    if (!color || !this._Background) return;

    this._Background.patch({
      color,
      w: this.w,
      h: this.h,
    });
  }

  private updateIcon() {
    if (!this._Icon) return;

    const texture = this.selected ? this.icons.selected : this.icons.unselected;
    this._Icon.patch({ ...texture, x: this.w / 2, y: this.h / 2 });
  }
}
